import { useEffect, useState, useCallback, useRef } from 'react';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';

export type ApiMessage = {
  message_id: string;
  thread_id: string;
  parent_message_id: string | null;
  author_id: string;
  content: string;
  attachments?: any;
  created_at: string;
  edited: boolean;
  edited_at: string | null;
  // ⬇ allow soft-delete tombstones from API
  deleted?: boolean;
};

type Page = { prev?: string; next?: string; hasMorePrev: boolean; hasMoreNext: boolean };
type Permissions = { canView: boolean; canPost: boolean; canEdit: boolean };
// Global map so even duplicate module instances share the lock
declare global {
  interface Window {
    __inflightDeletes?: Record<string, boolean>;
    __conversationCache?: Record<string, { promise: Promise<any>; timestamp: number }>;
  }
}

// helpers: filter tombstones & de-dupe + ASC
const filterTombstones = (list: ApiMessage[]) => (list ?? []).filter((m) => !m.deleted && m.content !== '__deleted__');

const dedupAsc = (list: ApiMessage[]) => {
  const map = new Map<string, ApiMessage>();
  for (const m of list) map.set(m.message_id, m);
  return Array.from(map.values()).sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
};

export function useConversation(contextKey: string | undefined, viewMode = false) {
  console.log(`[useConversation] CALLED with contextKey: ${contextKey}`, new Error().stack);
  const apiClients = useApiClientsOrNull();
  
  if (!apiClients) {
    return {
      threadId: undefined,
      messages: [],
      page: undefined,
      perms: { canView: false, canPost: false, canEdit: false },
      canPost: false,
      loading: false,
      err: null,
      loadOlder: async () => { return; },
      pollNew: async () => { return; },
      sendMessage: async (_content: string, _parentMessageId?: string, _mentionedUserIds?: string[]) => ({ success: false, message: 'API clients not available' }),
      editMessage: async () => ({ success: false, message: 'API clients not available' }),
      deleteMessage: async () => ({ success: false, message: 'API clients not available' }),
      isDeleting: () => false,
      refreshTail: async () => { return; }
    };
  }
  
  const { graphClient } = apiClients;
  const [threadId, setThreadId] = useState<string>();
  const [messages, setMessages] = useState<ApiMessage[]>([]);
  const [page, setPage] = useState<Page>();
  const [perms, setPerms] = useState<Permissions>({ canView: false, canPost: false, canEdit: false });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // snapshot for revert-on-failure
  const messagesRef = useRef<ApiMessage[]>([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  // shared across all mounts/instances
  const getInflightDeletes = () => {
    if (typeof window !== 'undefined') {
      const g = globalThis as unknown as { __inflightDeletes?: Record<string, boolean> };
      g.__inflightDeletes ??= {};
      return g.__inflightDeletes;
    }
    return {} as Record<string, boolean>;
  };
  const inflightDeletes = getInflightDeletes();

  // Global conversation cache for request deduplication
  const getConversationCache = () => {
    if (typeof window !== 'undefined') {
      const g = globalThis as unknown as {
        __conversationCache?: Record<string, { promise: Promise<any>; timestamp: number }>;
      };
      g.__conversationCache ??= {};
      return g.__conversationCache;
    }
    return {} as Record<string, { promise: Promise<any>; timestamp: number }>;
  };

  // track deleting state per id (optional spinner)
  const [deletingIds] = useState<Set<string>>(new Set());
  const isDeleting = useCallback((id: string) => deletingIds.has(id), [deletingIds]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!contextKey) return;
      
      const cache = getConversationCache();
      const cacheKey = `thread_${contextKey}`;
      const now = Date.now();
      
      // Check if we already have a recent request in flight or cached (within 5 seconds)
      if (cache[cacheKey] && (now - cache[cacheKey].timestamp < 5000)) {
        console.log(`[useConversation] Using cached/in-flight request for contextKey: ${contextKey}`);
        try {
          const result = await cache[cacheKey].promise;
          if (cancelled) return;
          
          const p = result?.thread?.permissions ?? result?.permissions ?? {};
          setPerms({ canView: !!p.canView, canPost: !!p.canPost, canEdit: !!p.canEdit });
          setThreadId(result.thread?.id);
          
          if (result.thread?.id && p.canView && result.messages) {
            setMessages(dedupAsc(filterTombstones(result.messages)));
            setPage(result.page);
          } else {
            setMessages([]);
            setPage(undefined);
          }
          setLoading(false);
          return;
        } catch (e: any) {
          // If cached request failed, we'll make a new one below
          console.debug('Cached conversation request failed, will retry:', e instanceof Error ? e.message : String(e));
          delete cache[cacheKey];
        }
      }
      
      setLoading(true);
      setErr(null);
      
      // Create new request and cache it
      const requestPromise = (async () => {
        console.log(`[useConversation] Making API call for contextKey: ${contextKey}`);
        const threadRes = await graphClient.get(`/conversation/thread?contextKey=${encodeURIComponent(contextKey)}&createIfMissing=true`);
        
        let messagesRes = { messages: [], page: undefined };
        const p = threadRes?.thread?.permissions ?? threadRes?.permissions ?? {};
        const id = threadRes.thread?.id;
        
        if (id && p.canView) {
          messagesRes = await graphClient.get(`/conversation/thread/${id}/messages?limit=50`);
        }
        
        return {
          thread: threadRes.thread,
          permissions: p,
          messages: messagesRes.messages,
          page: messagesRes.page
        };
      })();
      
      cache[cacheKey] = { promise: requestPromise, timestamp: now };
      
      try {
        const result = await requestPromise;
        if (cancelled) return;

        setPerms({ canView: !!result.permissions.canView, canPost: !!result.permissions.canPost, canEdit: !!result.permissions.canEdit });
        setThreadId(result.thread?.id);

        if (result.thread?.id && result.permissions.canView) {
          setMessages(dedupAsc(filterTombstones(result.messages)));
          setPage(result.page);
        } else {
          setMessages([]);
          setPage(undefined);
        }
      } catch (e: any) {
        if (!cancelled) setErr(e?.message ?? 'Failed to load conversation');
        delete cache[cacheKey]; // Remove failed request from cache
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contextKey, graphClient]);

  const loadOlder = useCallback(async () => {
    if (!threadId || !page?.prev || !perms.canView) return;
    const res = await graphClient.get(`/conversation/thread/${threadId}/messages?limit=50&before=${encodeURIComponent(page.prev)}`);
    const incoming = filterTombstones(res.messages);
    setMessages((prev) => dedupAsc([...incoming, ...prev])); // prepend (+ dedup)
    setPage(res.page);
  }, [graphClient, threadId, page?.prev, perms.canView]);

  const pollNew = useCallback(async () => {
    if (!threadId || !page?.next || !perms.canView) return;
    const res = await graphClient.get(`/conversation/thread/${threadId}/messages?limit=50&after=${encodeURIComponent(page.next)}`);
    const incoming = filterTombstones(res.messages);
    if (incoming.length) setMessages((prev) => dedupAsc([...prev, ...incoming])); // append (+ dedup)
    setPage(res.page);
  }, [graphClient, threadId, page?.next, perms.canView]);

  // refetch tail; optionally exclude ids to avoid flicker during eventual consistency
  const refreshTail = useCallback(
    async (excludeIds: string[] = [], tries = 4) => {
      if (!threadId || !perms.canView) return;
      const listRes = await graphClient.get(`/conversation/thread/${threadId}/messages?limit=50`);
      const fetched = filterTombstones(listRes.messages);
      const filtered = fetched.filter((m) => !excludeIds.includes(m.message_id));
      setMessages(dedupAsc(filtered));
      setPage(listRes.page);

      // if backend still returns excluded ids (replica lag), retry briefly
      const stillPresent = excludeIds.some((id) => fetched.some((m) => m.message_id === id));
      if (stillPresent && tries > 0) {
        await new Promise((r) => setTimeout(r, 300));
        return refreshTail(excludeIds, tries - 1);
      }
    },
    [graphClient, threadId, perms.canView]
  );

  const sendMessage = useCallback(
    async (content: string, parentMessageId?: string, mentionedUserIds?: string[]) => {
      if (!threadId || !perms.canPost || viewMode) return;
      const body: any = { content };
      if (parentMessageId) body.parentMessageId = parentMessageId;
      if (mentionedUserIds && mentionedUserIds.length > 0) body.mentionedUserIds = mentionedUserIds;

      const created = await graphClient.post(`/conversation/thread/${threadId}/messages`, body);
      setMessages((prev) => dedupAsc([...prev, created])); // instant append
      return created;
    },
    [graphClient, threadId, perms.canPost, viewMode]
  );

  const editMessage = useCallback(
    async (messageId: string, content: string) => {
      if (!threadId || !perms.canEdit || viewMode) return;
      const updated = await graphClient.put(`/conversation/thread/${threadId}/messages/${messageId}`, { content });
      setMessages((prev) => prev.map((m) => (m.message_id === messageId ? updated : m)));
      return updated;
    },
    [graphClient, threadId, perms.canEdit, viewMode]
  );

  // ✅ Optimistic delete + exclude-on-refresh + retry
  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!threadId || !perms.canEdit || viewMode) return;

      // hard guard: do nothing if already deleting this id
      if (inflightDeletes[messageId]) return;
      inflightDeletes[messageId] = true;

      const snapshot = messagesRef.current;
      setMessages((list) => list.filter((m) => m.message_id !== messageId)); // optimistic

      try {
        await graphClient.delete(`/conversation/thread/${threadId}/messages/${messageId}`);
        await refreshTail([messageId], 5); // keep it gone if your backend is eventually consistent
      } catch (error: unknown) {
        // If server is already deleted and returns 404, treat it as success (idempotent)
        const is404 = error && typeof error === 'object' && (
          ('status' in error && error.status === 404) ||
          ('response' in error && error.response && typeof error.response === 'object' && 'status' in error.response && error.response.status === 404)
        );
        if (!is404) {
          setMessages(snapshot); // revert only on real failures
          throw error;
        }
        // no-op: we already removed it locally
      } finally {
        delete inflightDeletes[messageId];
      }
    },
    [graphClient, threadId, perms.canEdit, viewMode, refreshTail]
  );

  return {
    threadId,
    messages,
    page,
    perms,
    canPost: perms.canPost,
    loading,
    err,
    loadOlder,
    pollNew,
    sendMessage,
    editMessage,
    deleteMessage,
    isDeleting,
    refreshTail,
  };
}
